import { useState, useCallback } from 'react';
import { toast } from '../../utils/toast';
import { extractApiError } from '../../utils/apiError';
import { copyTextToClipboard } from '../../utils/copyToClipboard';
import { Button } from '../ui/Button';
import { FiX, FiCopy, FiLink, FiMail, FiLock, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import {
  useListEventInvites,
  useGetEventInviteLink,
  useSendEventInvites,
  useResendEventInvite,
  useRevokeEventInvite,
  useCheckUsernameAvailability,
} from '../../api/events';

import { useAuth } from '../../hooks/useAuth';

interface ManagePrivateAccessModalProps {
  eventId: string | number;
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'invites' | 'link';

const WEB_FRONTEND_URL = import.meta.env.VITE_CHECKOUT_BASE_URL || 'https://connect.musicade.app';

function transformLink(backendLink: string): string {
  const pathStart = backendLink.indexOf('/events/private/');
  if (pathStart !== -1) {
    return `${WEB_FRONTEND_URL.replace(/\/+$/, '')}${backendLink.substring(pathStart)}`;
  }
  return backendLink;
}

export function ManagePrivateAccessModal({
  eventId,
  isOpen,
  onClose,
}: ManagePrivateAccessModalProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('invites');

  // ── Email Invites ──
  const [inviteInput, setInviteInput] = useState('');
  const [pendingInvitees, setPendingInvitees] = useState<{ email: string }[]>([]);
  const [resolutionErrors, setResolutionErrors] = useState<Record<string, string>>({});
  const [isResolving, setIsResolving] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'resend' | 'revoke';
    inviteId: number;
  } | null>(null);

  const {
    data: invites,
    isLoading: invitesLoading,
    refetch: refetchInvites,
  } = useListEventInvites(eventId);
  const sendInvites = useSendEventInvites(eventId);
  const resendInvite = useResendEventInvite();
  const revokeInvite = useRevokeEventInvite();
  const checkUsername = useCheckUsernameAvailability();

  // ── Access Link ──
  const {
    data: linkData,
    isLoading: linkLoading,
    refetch: refetchLink,
  } = useGetEventInviteLink(eventId);

  // ── Resolve a single @username token ──
  const resolveUsername = useCallback(
    async (username: string): Promise<boolean> => {
      try {
        const result = await checkUsername.mutateAsync(username);
        if (!result.available) {
          return true; // user exists
        }
        setResolutionErrors((prev) => ({
          ...prev,
          [username]: `Username "${username}" not found`,
        }));
        return false;
      } catch {
        setResolutionErrors((prev) => ({
          ...prev,
          [username]: 'Failed to verify username',
        }));
        return false;
      }
    },
    [checkUsername],
  );

  // ── Check if a token is already invited (active/purchased) ──
  const isAlreadyInvited = useCallback(
    (token: string): boolean => {
      if (!invites || invites.length === 0) return false;
      const activeInvites = invites.filter(
        (inv) => inv.status === 'active' || inv.status === 'purchased',
      );
      // For @username tokens, the backend invite list uses email — we can only match by email
      // For email tokens, do a direct email comparison
      if (token.includes('@') && !token.startsWith('@')) {
        return activeInvites.some((inv) => inv.email.toLowerCase() === token.toLowerCase());
      }
      // For @username tokens we can't resolve to email here without an extra API call,
      // so we skip this check and let the send fail gracefully on the backend
      return false;
    },
    [invites],
  );

  // ── Parse and resolve input ──
  const handleInviteInputSubmit = useCallback(async () => {
    const tokens = inviteInput
      .split(/[,\n;]/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (tokens.length === 0) return;

    setIsResolving(true);

    for (const token of tokens) {
      if (user) {
        if (
          token.toLowerCase() === user.email.toLowerCase() ||
          token.toLowerCase() === `@${user.username?.toLowerCase()}`
        ) {
          toast.error('You cannot invite yourself.');
          continue;
        }
      }
      // Skip if already staged
      if (pendingInvitees.some((p) => p.email === token)) continue;
      // Skip if already invited with active/purchased status
      if (isAlreadyInvited(token)) continue;

      if (token.includes('@') && !token.startsWith('@')) {
        setPendingInvitees((prev) => [...prev, { email: token }]);
      } else if (token.startsWith('@')) {
        const username = token.slice(1);
        const exists = await resolveUsername(username);
        if (exists) {
          setPendingInvitees((prev) => [...prev, { email: token }]);
        }
      } else {
        setResolutionErrors((prev) => ({
          ...prev,
          [token]: `"${token}" is not a valid email or username`,
        }));
      }
    }

    setInviteInput('');
    setIsResolving(false);
  }, [inviteInput, pendingInvitees, resolveUsername, isAlreadyInvited, user]);

  if (!isOpen) return null;

  const handleClose = () => {
    setInviteInput('');
    setPendingInvitees([]);
    setResolutionErrors({});
    setConfirmAction(null);
    setActiveTab('invites');
    onClose();
  };

  const handleSendInvites = async () => {
    if (pendingInvitees.length === 0) return;

    const emails = pendingInvitees
      .filter((g) => g.email.includes('@') && !g.email.startsWith('@'))
      .map((g) => g.email);
    const usernames = pendingInvitees
      .filter((g) => g.email.startsWith('@'))
      .map((g) => g.email.slice(1));

    sendInvites.mutate(
      { guest_emails: emails, guest_usernames: usernames },
      {
        onSuccess: () => {
          toast.success('Invites sent successfully');
          setPendingInvitees([]);
          refetchInvites();
        },
        onError: (err: unknown) => {
          toast.error(extractApiError(err, 'Failed to send invites'));
        },
      },
    );
  };

  const handleResend = (inviteId: number) => {
    resendInvite.mutate(inviteId, {
      onSuccess: () => {
        toast.success('Invite resent');
        refetchInvites();
        setConfirmAction(null);
      },
      onError: () => {
        toast.error('Failed to resend invite');
        setConfirmAction(null);
      },
    });
  };

  const handleRevoke = (inviteId: number) => {
    revokeInvite.mutate(inviteId, {
      onSuccess: () => {
        toast.success('Invite revoked');
        refetchInvites();
        setConfirmAction(null);
      },
      onError: () => {
        toast.error('Failed to revoke invite');
        setConfirmAction(null);
      },
    });
  };

  const handleCopyLink = async (link: string) => {
    const success = await copyTextToClipboard(link);
    if (success) {
      toast.success('Link copied to clipboard');
      refetchLink();
    } else {
      toast.error('Failed to copy link');
    }
  };

  const displayLink = linkData?.link ? transformLink(linkData.link) : null;

  const inviteCount = invites?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4 sm:py-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Panel */}
      <div className="relative w-full sm:max-w-lg max-h-[85dvh] bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/8 shrink-0">
          <h2 className="text-white text-lg font-semibold">Manage Private Access</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/8 text-zinc-400 hover:text-white transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/8 shrink-0">
          <button
            onClick={() => setActiveTab('invites')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-[14px] font-medium transition-colors ${
              activeTab === 'invites'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <FiMail size={14} />
            Email Invites
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-[14px] font-medium transition-colors ${
              activeTab === 'link'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <FiLink size={14} />
            Access Link
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          {/* ── Email Invites Tab ── */}
          {activeTab === 'invites' && (
            <>
              {/* Add invitee input area */}
              <div className="space-y-3">
                <p className="text-white text-sm font-medium">Invite by email or @username</p>
                <div className="bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3 min-h-[100px]">
                  {pendingInvitees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {pendingInvitees.map((g, i) => (
                        <span
                          key={`${g.email}-${i}`}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${
                            g.email.startsWith('@')
                              ? 'bg-purple-600/20 border-purple-500/40 text-purple-300'
                              : 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                          }`}
                        >
                          {g.email.startsWith('@') && <FiLock size={12} />}
                          {g.email}
                          <button
                            type="button"
                            onClick={() =>
                              setPendingInvitees((prev) => prev.filter((_, idx) => idx !== i))
                            }
                            className="hover:text-white transition-colors"
                          >
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={inviteInput}
                    onChange={(e) => setInviteInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        if (e.key === ',') {
                          const tokens = (inviteInput + ',')
                            .split(/[,\n;]/)
                            .map((s) => s.trim())
                            .filter(Boolean);
                          const last = tokens[tokens.length - 1];
                          if (last && (last.includes('@') || last.startsWith('@'))) {
                            setPendingInvitees((prev) => {
                              if (prev.some((g) => g.email === last)) return prev;
                              if (isAlreadyInvited(last)) return prev;
                              return [...prev, { email: last }];
                            });
                            setInviteInput('');
                          }
                        } else {
                          handleInviteInputSubmit();
                        }
                      }
                    }}
                    onBlur={() => {
                      const parts = inviteInput
                        .split(/[,\n;]/)
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const valid = parts.filter((p) => p.includes('@') || p.startsWith('@'));
                      if (valid.length > 0) {
                        const tokens = inviteInput
                          .split(/[,\n;]/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        tokens.forEach((token) => {
                          if (
                            !pendingInvitees.some((p) => p.email === token) &&
                            !isAlreadyInvited(token) &&
                            (token.includes('@') || token.startsWith('@'))
                          ) {
                            setPendingInvitees((prev) => [...prev, { email: token }]);
                          }
                        });
                        setInviteInput('');
                      }
                    }}
                    placeholder="name@example.com or @username, separate with commas"
                    rows={3}
                    className="w-full bg-transparent border-none text-white placeholder:text-zinc-600 focus:ring-0 focus:outline-none text-[14px] resize-none"
                  />
                </div>

                {Object.keys(resolutionErrors).length > 0 && (
                  <div className="space-y-1">
                    {Object.entries(resolutionErrors).map(([key, msg]) => (
                      <p key={key} className="text-red-400 text-xs flex items-center gap-1">
                        <FiAlertCircle size={12} />
                        {msg}
                      </p>
                    ))}
                  </div>
                )}

                <Button
                  onClick={handleSendInvites}
                  disabled={pendingInvitees.length === 0 || sendInvites.isPending || isResolving}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {sendInvites.isPending ? 'Sending…' : 'Send Invites'}
                </Button>
              </div>

              {/* Invite List */}
              <div className="border-t border-white/8 pt-4">
                <p className="text-white text-sm font-medium mb-3">
                  Invited Guests ({inviteCount})
                </p>

                {invitesLoading && (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  </div>
                )}

                {!invitesLoading && inviteCount === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-8">No guests invited yet</p>
                )}

                {!invitesLoading &&
                  invites?.map((invite) => {
                    const status = invite.status?.toLowerCase() ?? '';
                    const isActive = status === 'active';
                    const isRevoked = status === 'revoked';
                    return (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                      >
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-[14px] truncate ${
                              isRevoked ? 'text-zinc-500 line-through' : 'text-white'
                            }`}
                          >
                            {invite.email}
                          </p>
                          <span
                            className={`inline-block text-[11px] font-medium mt-0.5 px-2 py-0.5 rounded-full ${
                              isActive
                                ? 'bg-blue-500/20 text-blue-400'
                                : status === 'purchased'
                                  ? 'bg-green-500/20 text-green-400'
                                  : isRevoked
                                    ? 'bg-red-500/10 text-red-400'
                                    : 'bg-zinc-700/50 text-zinc-500'
                            }`}
                          >
                            {isRevoked ? 'Revoked' : status || invite.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          {isActive && (
                            <>
                              {!invite.is_automatic && !invite.automatic && (
                                <button
                                  onClick={() =>
                                    setConfirmAction({
                                      type: 'resend',
                                      inviteId: invite.invite_id ?? invite.id,
                                    })
                                  }
                                  className="text-[12px] text-blue-400 hover:text-blue-300 font-medium px-2 py-1 rounded-full hover:bg-blue-500/10 transition-colors"
                                >
                                  Resend
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  setConfirmAction({
                                    type: 'revoke',
                                    inviteId: invite.invite_id ?? invite.id,
                                  })
                                }
                                className="text-[12px] text-red-400 hover:text-red-300 font-medium px-2 py-1 rounded-full hover:bg-red-500/10 transition-colors"
                              >
                                Revoke
                              </button>
                            </>
                          )}
                          {/* No actions for revoked or purchased invites */}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}

          {/* ── Access Link Tab ── */}
          {activeTab === 'link' && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  <FiLink size={18} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Shareable Invite Link</p>
                  <p className="text-zinc-500 text-xs">
                    Anyone with this link can purchase tickets
                  </p>
                </div>
              </div>

              {linkLoading && (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              )}

              {!linkLoading && displayLink && (
                <div className="space-y-3">
                  <div className="bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3">
                    <p className="text-zinc-300 text-[13px] break-all">{displayLink}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleCopyLink(displayLink)}
                      className="flex-1 rounded-full gap-2"
                      size="lg"
                    >
                      <FiCopy size={14} />
                      Copy Link
                    </Button>
                    <button
                      onClick={() => refetchLink()}
                      className="w-12 h-12 flex items-center justify-center rounded-full bg-zinc-800 border border-white/10 text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
                      title="Generate new link"
                    >
                      <FiRefreshCw size={16} />
                    </button>
                  </div>
                </div>
              )}

              {!linkLoading && !displayLink && (
                <div className="text-center py-8">
                  <p className="text-zinc-500 text-sm mb-4">No access link generated yet</p>
                  <Button
                    onClick={() => {
                      refetchLink();
                    }}
                    className="rounded-full"
                    variant="secondary"
                  >
                    Generate Shareable Link
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Confirmation Dialog ── */}
        {confirmAction && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center rounded-3xl z-10">
            <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 mx-4 max-w-xs w-full shadow-2xl">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
                  <FiAlertCircle
                    size={24}
                    className={confirmAction.type === 'revoke' ? 'text-red-400' : 'text-blue-400'}
                  />
                </div>
              </div>
              <p className="text-white text-center font-medium mb-1">
                {confirmAction.type === 'resend' ? 'Resend Invite?' : 'Revoke Invite?'}
              </p>
              <p className="text-zinc-500 text-sm text-center mb-5">
                {confirmAction.type === 'resend'
                  ? 'This guest will receive another invitation email.'
                  : 'This guest will no longer have access to the event.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 py-3 rounded-full border border-white/10 text-zinc-300 text-[14px] font-medium hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmAction.type === 'resend') {
                      handleResend(confirmAction.inviteId);
                    } else {
                      handleRevoke(confirmAction.inviteId);
                    }
                  }}
                  className={`flex-1 py-3 rounded-full text-[14px] font-medium transition-colors ${
                    confirmAction.type === 'revoke'
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-blue-600 text-white hover:bg-blue-500'
                  }`}
                >
                  {confirmAction.type === 'resend' ? 'Resend' : 'Revoke'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Safe area bottom padding for mobile */}
        <div className="shrink-0 pb-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}
